import { Notification } from "../services/daeApi"

/**
 * An action link on a notification card.
 */
interface ActionLink {
  to: string
  label: string
}

/**
 * A notification's primary (study or DTA) and secondary (type-specific) actions.
 */
interface NotificationActions {
  primary: ActionLink | null
  secondary: ActionLink | null
}

/**
 * Build an ActionLink from notification context, or null when required IDs are missing.
 */
type TemplateFn = (ctx: { studyId: string; dtaId?: string; runId?: string }) => ActionLink | null

/**
 * Actions for one notification type. An unset primary falls back to viewStudy when there is a
 * studyId.
 */
interface ActionConfig {
  primary?: TemplateFn
  secondary?: TemplateFn
}

const ARROW_SUFFIX = " →"

const viewStudy: TemplateFn = ({ studyId }) => ({
  to: `/studies/${studyId}`,
  label: "View Study",
})

const viewDta: TemplateFn = ({ dtaId }) =>
  dtaId ? { to: `/dtas/${dtaId}`, label: "View DTA" } : null

const viewIngestion: TemplateFn = ({ studyId }) => ({
  to: `/studies/${studyId}?tab=ingestion`,
  label: "View Ingestion",
})

const viewRun: TemplateFn = ({ studyId, runId }) =>
  runId ? { to: `/studies/${studyId}/runs/${runId}`, label: "View Run" } : null

const createDta: TemplateFn = ({ studyId }) => ({
  to: `/studies/${studyId}?action=create`,
  label: "Create DTA",
})

const reviewDta: TemplateFn = ({ dtaId }) =>
  dtaId ? { to: `/dtas/${dtaId}`, label: "Review DTA" } : null

const uploadData: TemplateFn = ({ dtaId }) =>
  dtaId ? { to: `/dtas/${dtaId}/submit`, label: "Upload Data" } : null

/**
 * Actions per notification_type. Keys must match the types the notification-event-router and
 * notification-dashboard-channel lambdas store. Unknown types get only a "View Study" primary.
 */
const ACTION_CONFIG: Record<string, ActionConfig> = {
  // --- Study lifecycle ---
  STUDY_CREATED: {
    primary: viewStudy,
    secondary: ({ studyId }) => ({
      to: `/studies/${studyId}?tab=protocol`,
      label: "Upload Protocol",
    }),
  },
  STUDY_ACCESS_GRANTED: {
    primary: viewStudy,
    secondary: createDta,
  },
  INGESTION_STUDY_CREATED: {
    primary: viewStudy,
    secondary: createDta,
  },

  // --- Ingestion runs ---
  INGESTION_RUN_COMPLETED: {
    primary: viewIngestion,
    secondary: viewStudy,
  },
  INGESTION_RUN_FAILED_REVIEW: {
    primary: viewIngestion,
    secondary: viewRun,
  },

  // --- DTA workflow ---
  DTA_WORKFLOW_TRANSITION: {
    primary: viewStudy,
    secondary: viewDta,
  },
  DTA_APPROVAL_REQUEST: {
    primary: viewStudy,
    secondary: reviewDta,
  },
  DTA_REJECTION: {
    primary: viewStudy,
    secondary: viewDta,
  },
  DTA_READY_FOR_REVIEW: {
    primary: viewStudy,
    secondary: reviewDta,
  },
  DTA_READY_FOR_APPROVED: {
    primary: reviewDta,
    secondary: viewStudy,
  },
  DTA_RETURNED_FOR_REVIEW: {
    primary: viewStudy,
    secondary: viewDta,
  },
  DTA_ACTIVE: {
    primary: viewStudy,
    secondary: uploadData,
  },
  DTA_INACTIVE: {
    primary: viewStudy,
    secondary: viewDta,
  },

  // --- Document extraction ---
  DTA_EXTRACTION_COMPLETE: {
    primary: viewDta,
  },
  DTA_EXTRACTION_FAILED: {
    primary: viewStudy,
    secondary: viewDta,
  },
  PROTOCOL_EXTRACTION_COMPLETE: {
    primary: viewStudy,
    secondary: ({ studyId }) => ({
      to: `/studies/${studyId}?tab=protocol`,
      label: "Review Protocol",
    }),
  },
  PROTOCOL_EXTRACTION_FAILED: {
    primary: viewStudy,
    secondary: ({ studyId }) => ({
      to: `/studies/${studyId}?tab=protocol`,
      label: "View Protocol",
    }),
  },

  // --- Vendor collaboration ---
  VENDOR_ACCESS_GRANTED: {
    primary: viewStudy,
    secondary: viewDta,
  },
  VENDOR_PROPOSAL_SUBMITTED: {
    primary: viewStudy,
    secondary: ({ dtaId }) =>
      dtaId ? { to: `/dtas/${dtaId}?tab=collaboration`, label: "Review Proposals" } : null,
  },
  PROPOSAL_ACCEPTED: {
    primary: viewStudy,
    secondary: viewDta,
  },
  PROPOSAL_REJECTED: {
    primary: viewStudy,
    secondary: viewDta,
  },
  VENDOR_DATA_SUBMISSION: {
    primary: viewStudy,
    secondary: ({ dtaId }) =>
      dtaId ? { to: `/dtas/${dtaId}/submit`, label: "View Submissions" } : null,
  },
  VENDOR_FEEDBACK_SUBMITTED: {
    primary: viewStudy,
    secondary: viewDta,
  },
  DAE_COMMENT_REPLY: {
    primary: viewStudy,
    secondary: viewDta,
  },
  VENDOR_ACCESSED_WORKSPACE: {
    primary: viewStudy,
    secondary: viewDta,
  },

  // --- Vendor connections ---
  VENDOR_CONNECTION_REQUEST: {
    primary: viewStudy,
    secondary: viewDta,
  },
  VENDOR_CONNECTION_CONFIGURED: {
    primary: viewStudy,
    secondary: viewDta,
  },
  CONNECTION_REQUEST_APPROVED: {
    primary: viewStudy,
    secondary: viewDta,
  },
  CONNECTION_REQUEST_REJECTED: {
    primary: viewStudy,
    secondary: viewDta,
  },

  // --- System/errors ---
  EMAIL_DELIVERY_FAILURE: {
    primary: viewStudy,
  },
  SECURITY_ALERT: {
    primary: viewStudy,
  },
}

function withArrow(action: ActionLink | null): ActionLink | null {
  if (!action) return null
  return { ...action, label: action.label + ARROW_SUFFIX }
}

export function getNotificationActions(
  notification: Notification,
  isVendor: boolean = false,
): NotificationActions {
  const type = notification.notification_type || ""
  const studyId = notification.study_id
  const meta = notification.metadata as Record<string, string | undefined> | undefined
  const dtaId = meta?.dta_id || meta?.dtaId
  const runId = meta?.run_id || meta?.runId

  const ctx = { studyId, dtaId, runId }
  const config = ACTION_CONFIG[type]

  if (!config) {
    return {
      primary: isVendor ? null : withArrow(studyId ? viewStudy(ctx) : null),
      secondary: null,
    }
  }

  const secondary = withArrow(config.secondary?.(ctx) ?? null)

  if (isVendor) {
    return { primary: null, secondary }
  }

  const primary = withArrow(config.primary?.(ctx) ?? (studyId ? viewStudy(ctx) : null))

  return { primary, secondary }
}
